import { MapContainer, TileLayer, CircleMarker, Polyline, Polygon, Popup } from 'react-leaflet';
import { Link } from 'react-router-dom';
import 'leaflet/dist/leaflet.css';

const SEV = { red: '#E11D48', orange: '#F7941D', yellow: '#FBBF24' };

export function AlertsMap({ alerts = [], restrictions = [], quakes = [], height = 340, center = [-41.2, 172.5], zoom = 5 }) {
  const pts = alerts.filter((a) => a.lat && a.lng && a.geometry_type !== 'polygon');
  const lines = alerts.filter((a) => a.geometry_type === 'line' && Array.isArray(a.path) && a.path.length && a.path.every((seg) => Array.isArray(seg) && seg.length >= 2 && Array.isArray(seg[0])));
  const polys = alerts.filter((a) => a.geometry_type === 'polygon' && Array.isArray(a.path) && a.path.length);
  const resPts = restrictions.filter((r) => r.lat && r.lng);
  const quakePts = quakes.filter((q) => q.lat && q.lng);
  const quakeColor = (m) => (m >= 5 ? '#dc2626' : m >= 4 ? '#ea580c' : '#7c3aed');
  return (
    <div className="rounded-2xl overflow-hidden border border-zinc-200 shadow-sm isolate" style={{ height }} data-testid="alerts-map">
      <MapContainer center={center} zoom={zoom} style={{ height: '100%', width: '100%' }} scrollWheelZoom={false}>
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution="&copy; OpenStreetMap"
        />
        {polys.map((a) =>
          a.path.map((ring, i) => (
            <Polygon
              key={`poly-${a.id}-${i}`}
              positions={ring}
              pathOptions={{ color: SEV[a.severity] || '#71717a', weight: 2, opacity: 0.9, fillColor: SEV[a.severity] || '#71717a', fillOpacity: 0.18 }}
              data-testid={`weather-poly-${a.id}`}
            >
              <Popup>
                <div className="text-xs">
                  <div className="font-bold">🌦 {a.event_type}</div>
                  <div>{a.location_area}</div>
                  <div className="text-zinc-500 mt-1">{a.status} · MetService</div>
                  <Link to={`/alerts/${a.id}`} className="mt-1.5 inline-flex items-center gap-1 font-semibold text-teal-700 hover:underline" data-testid={`map-details-${a.id}`}>View full details →</Link>
                </div>
              </Popup>
            </Polygon>
          ))
        )}
        {lines.map((a) =>
          a.path.map((seg, i) => (
            <Polyline
              key={`line-${a.id}-${i}`}
              positions={seg}
              pathOptions={{ color: SEV[a.severity] || '#71717a', weight: a.severity === 'red' ? 6 : 4, opacity: 0.85 }}
              data-testid={`alert-line-${a.id}`}
            >
              <Popup>
                <div className="text-xs">
                  <div className="font-bold">{a.event_type}{a.highway ? ` · ${a.highway}` : ''}</div>
                  <div>{a.location_area}</div>
                  <div className="text-zinc-500 mt-1">{a.status}</div>
                  <Link to={`/alerts/${a.id}`} className="mt-1.5 inline-flex items-center gap-1 font-semibold text-teal-700 hover:underline" data-testid={`map-details-${a.id}`}>View full details →</Link>
                </div>
              </Popup>
            </Polyline>
          ))
        )}
        {pts.map((a) => (
          <CircleMarker
            key={a.id}
            center={[a.lat, a.lng]}
            radius={a.severity === 'red' ? 9 : a.severity === 'orange' ? 7 : 5}
            pathOptions={{
              color: SEV[a.severity] || '#71717a',
              fillColor: SEV[a.severity] || '#71717a',
              fillOpacity: 0.7,
              weight: 2,
            }}
          >
            <Popup>
              <div className="text-xs">
                <div className="font-bold">{a.event_type}{a.highway ? ` · ${a.highway}` : ''}</div>
                <div>{a.location_area}</div>
                <div className="text-zinc-500 mt-1">{a.status}</div>
                <Link to={`/alerts/${a.id}`} className="mt-1.5 inline-flex items-center gap-1 font-semibold text-teal-700 hover:underline" data-testid={`map-details-${a.id}`}>View full details →</Link>
              </div>
            </Popup>
          </CircleMarker>
        ))}
        {resPts.map((r) => (
          <CircleMarker
            key={`res-${r.id}`}
            center={[r.lat, r.lng]}
            radius={5}
            pathOptions={{ color: '#111827', fillColor: '#fbbf24', fillOpacity: 0.9, weight: 2 }}
          >
            <Popup>
              <div className="text-xs">
                <div className="font-bold">{r.name}</div>
                <div>{r.highway || r.highway_raw} · {r.condition || 'restriction'}{r.axle ? ` · axle ${r.axle}` : ''}</div>
                <Link to="/restrictions" className="mt-1.5 inline-flex items-center gap-1 font-semibold text-teal-700 hover:underline" data-testid={`map-restriction-details-${r.id}`}>View freight restrictions →</Link>
              </div>
            </Popup>
          </CircleMarker>
        ))}
        {quakePts.map((q) => {
          const mag = q.magnitude || 0;
          const col = quakeColor(mag);
          return (
            <CircleMarker
              key={`quake-${q.id}`}
              center={[q.lat, q.lng]}
              radius={Math.max(5, Math.min(16, mag * 2.4))}
              pathOptions={{ color: col, fillColor: col, fillOpacity: 0.35, weight: 2, dashArray: '3 3' }}
              data-testid={`quake-marker-${q.id}`}
            >
              <Popup>
                <div className="text-xs">
                  <div className="font-bold">🌐 M{mag.toFixed(1)} earthquake</div>
                  <div>{q.locality}</div>
                  <div className="text-zinc-500 mt-1">Depth {Math.round(q.depth || 0)} km · GeoNet</div>
                </div>
              </Popup>
            </CircleMarker>
          );
        })}
      </MapContainer>
    </div>
  );
}
